import type { JSX } from 'react';

/**
 * Compact icon set for the micro-popup kind badge + chrome. One
 * stroke-based 24×24 viewBox per kind so SVG fill picks up
 * {@code currentColor}.
 */
const wrap = (path: JSX.Element): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {path}
  </svg>
);

export const PlayCircleIcon = () =>
  wrap(
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M10 8l6 4-6 4V8z" fill="currentColor" stroke="none" />
    </>,
  );
export const QuizIcon = () =>
  wrap(
    <>
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <circle cx="12" cy="17" r="0.5" fill="currentColor" />
    </>,
  );
export const BookIcon = () =>
  wrap(<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5V19.5z" />);
export const InteractiveIcon = () =>
  wrap(
    <>
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="10" />
    </>,
  );
export const InfoCardsIcon = () =>
  wrap(
    <>
      <rect x="3" y="6" width="14" height="14" rx="2" />
      <rect x="7" y="3" width="14" height="14" rx="2" />
    </>,
  );
export const ChecklistIcon = () =>
  wrap(
    <>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </>,
  );
export const AudioIcon = () =>
  wrap(
    <>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
      <path d="M19 12a4 4 0 0 0-1.17-2.83" />
    </>,
  );
export const DownloadIcon = () =>
  wrap(
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </>,
  );
export const ScenarioIcon = () =>
  wrap(
    <>
      <circle cx="6" cy="6" r="3" />
      <circle cx="18" cy="18" r="3" />
      <path d="M6 9v6a3 3 0 0 0 3 3h6" />
    </>,
  );
export const LearningPathIcon = () =>
  wrap(
    <>
      <path d="M3 12h4l3-9 4 18 3-9h4" />
    </>,
  );
export const ReflectionIcon = () =>
  wrap(
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />,
  );
export const FlashcardIconSmall = () =>
  wrap(
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </>,
  );
export const LinkIcon = () =>
  wrap(
    <>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </>,
  );
export const BookmarkIcon = () =>
  wrap(<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />);
export const CloseIcon = () =>
  wrap(
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>,
  );

// Large check/cross pair for the quiz true/false chips.
const wrapBig = (path: JSX.Element): JSX.Element => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {path}
  </svg>
);

export const CheckBigIcon = () => wrapBig(<polyline points="20 6 9 17 4 12" />);
export const CrossBigIcon = () =>
  wrapBig(
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>,
  );
